'use client';

/**
 * **One attribute rendered as chrome — the value alone, with no label column**
 * (VALE-75 / [INVDRAW-4]; ported from LF-6314's prototype `AttributeChromeValue`).
 *
 * The treatment has a vocabulary for a record's *fields* and none for a
 * record-scoped attribute that belongs to the drawer's header. The inventory
 * drawer needs exactly that for `Status`: production already shows it beside the
 * title, it is the most-scanned fact on the surface, and as the last row of the
 * grid it was the thing a reader reached last.
 *
 * ## Why not simply put a one-row `AttributeGrid` in the header
 *
 * That was the prototype's first attempt and it rendered **empty** on half the
 * records. The label column is
 * `clamp(21 * --spacing, 45cqw - 45px, 45 * --spacing)` — a container-query
 * function of the grid ({@link ATTRIBUTE_LABEL_WIDTH}) — so inside a header
 * container narrower than a form column it collapses to its 71.4px floor and the
 * value cell is left with almost nothing. Measured on the prototype: a bare
 * rounded box with no text on the short-titled records, a wide box with a long
 * empty tail on the others. The grid is built for a column of rows and says so;
 * chrome needs the other half of the row.
 *
 * ## So this is the grid's row, minus the label
 *
 * The read-out, the editor, the cell chrome and — load-bearing — the **commit
 * path** are the shared ones: {@link useAttributeCommit} over a one-field list,
 * so the pill, the hover target, commit-on-blur, `Escape`-reverts and the
 * rejected-commit contract (§2 — the field re-opens with the typing intact and
 * says why) are identical to every other closed-vocabulary row. Re-deriving any
 * of that here is the duplicate-source-of-truth failure the root `CLAUDE.md`
 * names, and the rejection contract in particular is the one this surface cannot
 * afford to reimplement: a swallowed rejection is indistinguishable from a save
 * that worked.
 *
 * The label is omitted because a `Status` label beside a status pill states the
 * same thing twice.
 *
 * ## It hugs a pill and fills for prose, derived rather than declared
 *
 * Hugging is right for what chrome was built for — a `Status` stretched across a
 * header reads as an unstyled input — and wrong for a value that is a sentence:
 * the cell is then only as wide as the text, so its EDITOR opens that wide too
 * and a long value is typed into a box a fraction of the room available
 * (VALE-1740, found at sign-off on the Scribe note).
 *
 * So which one a value gets is derived from the value, exactly as §2 derives the
 * other affordances: a value that {@link valueWraps} — prose, declared by
 * `wraps` or `fullWidth` — fills its box, and everything else hugs. The four
 * shipped consumers are all `select` rows that wrap nothing, so none of them
 * moves.
 */

import {
  AttributeTriStateSegmented,
  isLiveTriState
} from './attribute-boolean';
import { useEffect, useId, useMemo } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AttributeFieldControl } from './attribute-field-control';
import { AttributeReadOut, valueWraps } from './attribute-read-out';
import { ATTRIBUTE_MUTED_INK, ATTRIBUTE_ROW_ERROR } from './attribute-tokens';
import type { AttributeDraft, AttributeSpec } from './attribute-types';
import { AttributeValueSlot, offersClear } from './attribute-value-cell';
import { clearDraft } from './attribute-grid';
import { useAttributeCommit } from './use-attribute-commit';
import { useAttributeSurfaceChannel } from './attribute-unsaved-state';

export function AttributeChromeValue<TPatch>({
  field,
  onSave,
  onEditingChange,
  row = false,
  testId
}: {
  field: AttributeSpec<TPatch>;
  /**
   * **A grid row's value, rendered where its label lives elsewhere**
   * (VALE-1845). A comparison grid, which names a fact once in its first
   * column and holds one value per person beside it, is still a column of
   * rows: so the cell fills its column as a grid row's does, and takes the
   * row's derived `×` (`offersClear`), committing through the same
   * `open` → `commitAndClose` pair the grid uses. A statement about the host,
   * not a per-field affordance, which is why it is not a `clearable` opt-in.
   * Off by default, so header chrome renders exactly as it did.
   */
  row?: boolean;
  /** Commits the patch. **Must reject to surface a failure** (§6). */
  onSave: (patch: TPatch) => Promise<unknown>;
  /** Fires as the editor opens and closes, so a drawer can keep `Escape`. */
  onEditingChange?: (editing: boolean) => void;
  testId?: string;
}) {
  /*
    A stable one-field list: `useAttributeCommit` keys its "did this actually
    change?" check off `fields[index].draft`, and rebuilding the array every
    render would be a new identity on each keystroke.
  */
  const fields = useMemo(() => [field], [field]);

  /*
    Chrome reports its unsaved state exactly as a grid does (VALE-349) — a
    `Status` a refusal is holding open is as much lost state as a row's, and the
    drawer it sits in is the thing that has to stop and ask. No-op outside a
    provider, which is every host that cannot be closed.
  */
  const {
    report: reportUnsaved,
    reportUncommitted,
    register
  } = useAttributeSurfaceChannel();
  const chromeId = useId();

  const {
    openIndex,
    draft,
    failure,
    saving,
    setDraft,
    open,
    revert,
    commitAndClose,
    flush
  } = useAttributeCommit<TPatch, AttributeDraft>({
    fields,
    onSave,
    onEditingChange,
    onUnsavedChange: (unsaved) => reportUnsaved(chromeId, unsaved),
    onUncommittedDraftChange: (holding) => reportUncommitted(chromeId, holding)
  });

  useEffect(
    () => () => {
      reportUnsaved(chromeId, false);
      reportUncommitted(chromeId, false);
    },
    [chromeId, reportUncommitted, reportUnsaved]
  );

  /*
    Chrome joins the surface's flush for the same reason it joins its unsaved
    report (VALE-1274): it runs the same commit path through the same editors,
    so a host's `Save` must reach it too. One treatment, one contract.
  */
  useEffect(() => {
    register(chromeId, { flush });
    return () => register(chromeId, null);
  }, [chromeId, flush, register]);

  const isOpen = openIndex === 0;

  /*
    A live tri-state is the control itself, as in the grid (VALE-1845): no
    read-out to open, no editor behind it. The answer still goes through
    `open` → `commitAndClose`, the one commit path. Without this arm a chrome
    tri-state fell through to the generic editor and rendered a text box.
  */
  if (isLiveTriState(field as AttributeSpec<unknown>)) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
        <AttributeTriStateSegmented
          field={field as AttributeSpec<unknown>}
          focused={false}
          onCommit={(next) => {
            open(0);
            commitAndClose(0, next);
          }}
          onStep={() => undefined}
        />
      </div>
    );
  }

  /* Prose fills its box; a pill hugs. See the docblock. A row always fills. */
  const hugs = !row && !valueWraps(field as AttributeSpec<unknown>);

  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      {/*
        `w-fit` while hugging, and load-bearing there: the cell's hug is
        `flex`-based, so it needs a parent that is not itself stretching.
        Without it the header's own layout hands the slot the title's width and
        the pill sits in a wide empty box that reads as an unstyled input — the
        defect the line was added for, and it returns if the wrapper goes.

        A prose value wants the opposite: `w-full`, so the value has the room to
        wrap at rest and its editor has it to be typed into.
      */}
      <div className={cn('flex max-w-full min-w-0', hugs ? 'w-fit' : 'w-full')}>
        <AttributeValueSlot
          unified
          field={field as AttributeSpec<unknown>}
          open={isOpen}
          live={false}
          /*
            **Chrome, so the cell hugs its value rather than filling a column**
            (VALE-349). Every grid row takes the column, which is what makes each
            row's hit area the same shape; a header has no column, and a `Status`
            pill stretched across one would read as an unstyled input.

            Derived rather than always true since VALE-1740 — see the docblock.
          */
          hugs={hugs}
          saving={saving.includes(field.label)}
          failed={failure !== null}
          onClear={
            row && offersClear(field as AttributeSpec<unknown>)
              ? (field.clearOverride ??
                (() => {
                  open(0);
                  commitAndClose(0, clearDraft(field));
                }))
              : undefined
          }
        >
          {isOpen ? (
            <AttributeFieldControl
              field={field}
              draft={draft}
              onDraft={setDraft}
              onRevert={revert}
              onCommit={(value) => commitAndClose(0, value)}
              /*
                Chrome is a single value, so there is nowhere to step to — `Tab`
                commits and closes rather than advancing to a next field that
                does not exist. Still the shared commit, so a refused value keeps
                the editor open with the typing intact exactly as it would in a
                grid.
              */
              onStep={(value) => commitAndClose(0, value)}
            />
          ) : (
            <AttributeReadOut
              field={field as AttributeSpec<unknown>}
              bare
              onActivate={() => open(0)}
              /*
                A chevron rather than a pencil: the value is a `select`, and a
                chevron is the one glyph that says *this opens a list of choices*
                rather than the vaguer *this is editable*. Hidden while the editor
                is open, where the control draws its own.
              */
              pillAffordance={
                field.editor.kind === 'select' ? (
                  <ChevronDownIcon
                    aria-hidden
                    className={cn(
                      'ml-0.5 h-3 w-3 shrink-0',
                      ATTRIBUTE_MUTED_INK
                    )}
                  />
                ) : undefined
              }
            />
          )}
        </AttributeValueSlot>
      </div>
      {/*
        The same refusal line a grid row renders under its cell, in the same
        token — bound to `failure` from the shared hook rather than to a second
        notion of "this did not save".

        It keeps the full sentence on screen where a row's does not: chrome has
        no label column to point with, so *"Status was not saved."* is the only
        thing naming the field.
      */}
      {failure ? (
        <p
          role="status"
          data-testid="attribute-chrome-save-error"
          className={ATTRIBUTE_ROW_ERROR}
        >
          {failure.label} was not saved. {failure.message}
        </p>
      ) : null}
    </div>
  );
}
